import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  UserIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface StudentProfile {
  id: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  avatar?: string;
  skills?: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  student: StudentProfile;
  onMessageSent?: () => void;
}

const ContactStudentModal: React.FC<Props> = ({ isOpen, onClose, student, onMessageSent }) => {
  const [messageType, setMessageType] = useState<'message' | 'job_invitation'>('message');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendMessage = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (messageType === 'job_invitation' && (!jobTitle.trim() || !jobDescription.trim())) {
      toast.error('Please fill in job title and description for job invitations');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const payload = {
        recipientId: student.id,
        subject,
        message,
        type: messageType,
        ...(messageType === 'job_invitation' && {
          jobTitle,
          jobDescription
        })
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        toast.success(messageType === 'job_invitation' ? 'Job invitation sent successfully!' : 'Message sent successfully!');
        onMessageSent?.();
        onClose();
        resetForm();
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error(error.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setJobTitle('');
    setJobDescription('');
    setMessageType('message');
  };

  const generateJobInvitationTemplate = () => {
    const studentName = student.displayName || `${student.firstName} ${student.lastName}`;
    return `Hi ${studentName},

I came across your profile and was impressed by your skills and experience. I'd like to invite you to apply for a position at our company.

Position: ${jobTitle || '[Job Title]'}

${jobDescription || '[Job Description]'}

If you're interested, please reply to this message and I'll provide more details about the role and our application process.

Looking forward to hearing from you!`;
  };

  React.useEffect(() => {
    if (messageType === 'job_invitation') {
      setSubject(`Job Opportunity: ${jobTitle || 'Position at our company'}`);
      setMessage(generateJobInvitationTemplate());
    }
  }, [messageType, jobTitle, jobDescription]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
              {student.avatar ? (
                <img src={student.avatar} alt="" className="h-10 w-10 rounded-full" />
              ) : (
                <UserIcon className="h-5 w-5 text-indigo-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Contact {student.displayName || `${student.firstName} ${student.lastName}`}
              </h3>
              <p className="text-sm text-gray-500">Send a message or job invitation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Message Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Message Type
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setMessageType('message')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  messageType === 'message'
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                General Message
              </button>
              <button
                onClick={() => setMessageType('job_invitation')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  messageType === 'job_invitation'
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <BriefcaseIcon className="h-5 w-5 mr-2" />
                Job Invitation
              </button>
            </div>
          </div>

          {/* Job Details (only for job invitations) */}
          {messageType === 'job_invitation' && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Job Details</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. Senior Software Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Description *
                  </label>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Brief description of the role, requirements, and benefits..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Message Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter message subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={messageType === 'job_invitation' ? 8 : 6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder={messageType === 'job_invitation' 
                  ? "This will be auto-generated based on job details, but you can customize it..."
                  : "Write your message here..."
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                {messageType === 'job_invitation' 
                  ? "Template is auto-generated but fully customizable"
                  : "Be professional and clear in your communication"
                }
              </p>
            </div>
          </div>

          {/* Skills Preview */}
          {student.skills && student.skills.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Student's Skills</h4>
              <div className="flex flex-wrap gap-2">
                {student.skills.slice(0, 8).map((skill, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full"
                  >
                    {skill}
                  </span>
                ))}
                {student.skills.length > 8 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{student.skills.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSendMessage}
            disabled={loading || !subject.trim() || !message.trim()}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                {messageType === 'job_invitation' ? 'Send Job Invitation' : 'Send Message'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactStudentModal;